import { ApiError } from '../api/client';

/** Same 403/400/fallback idiom `contract-api-errors.ts`'s `genericApiErrorMessage` already applies. */
function genericApiErrorMessage(error: ApiError): string {
  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/** `delivery-note-documents/spec.md` → codes from `DeliveryNoteExceptionFilter` (`design.md` §6). */
export function mapDeliveryNoteError(error: unknown): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (error instanceof Error && error.name === 'PendingCaptureError') {
    return 'Ya hay una firma guardada para este remito.';
  }

  if (error instanceof Error && error.name === 'UploadFailedError') {
    return 'No pudimos subir el archivo, probá de nuevo.';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 404 && code === 'DELIVERY_NOTE_BATCH_EMPTY') {
    return 'No hay remitos para esa fecha.';
  }

  if (error.status === 404) {
    return 'No encontramos lo que buscabas. Revisá el punto de venta elegido.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_INVALID_TRANSITION') {
    return 'Ese remito ya no admite esta acción en su estado actual.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_INVOICED_IMMUTABLE') {
    return 'Ese remito ya fue facturado y no admite cambios.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_LOCKED') {
    return 'Ese remito ya está despachado y no admite cambios en sus ítems.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_SIGNATURE_REQUIRED') {
    return 'Falta la firma del remito para esta acción.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_VOID_REASON_REQUIRED') {
    return 'El motivo de anulación es obligatorio.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_BATCH_LIMIT_EXCEEDED') {
    return 'Hay más de 200 remitos para esa fecha. Achicá el rango antes de imprimir el lote.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_UPLOAD_INVALID_TYPE') {
    return 'El archivo debe ser un PDF, JPEG o PNG.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_UPLOAD_TOO_LARGE') {
    return 'El archivo supera el tamaño máximo permitido (10 MB).';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_RECEIVER_NAME_REQUIRED') {
    return 'El nombre de quien recibe es obligatorio.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_SIGNATURE_IMAGE_INVALID') {
    return 'No pudimos leer la firma. Volvé a firmar e intentá de nuevo.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_SIGNATURE_IMAGE_DIMENSIONS') {
    return 'La firma tiene un tamaño fuera de rango. Volvé a firmar ocupando más espacio del recuadro.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_SIGNATURE_IMAGE_TOO_LARGE') {
    return 'La firma pesa demasiado. Volvé a firmar e intentá de nuevo.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_SIGNATURE_IMAGE_EMPTY') {
    return 'El trazo está vacío. Dibujá la firma antes de confirmar.';
  }

  if (error.status === 422 && code === 'DELIVERY_NOTE_SIGNATURE_DOCUMENT_INVALID') {
    return 'El DNI debe tener 7 u 8 dígitos.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_SIGNATURE_CONFLICT') {
    return 'Este remito ya fue firmado por otra persona en simultáneo.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_ALREADY_SIGNED') {
    return 'Este remito ya fue firmado en otro dispositivo. Tu firma no se registró.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_VOIDED') {
    return 'El remito fue anulado. Tu firma no se registró.';
  }

  if (error.status === 409 && code === 'DELIVERY_NOTE_CLIENT_CAPTURE_REUSED') {
    return 'Esa firma ya se había usado en otro remito. Volvé a firmar.';
  }

  return genericApiErrorMessage(error);
}
